"use client";

import React, { createContext, useContext, useEffect } from "react";
import posthog from "posthog-js";
import { useAuth } from "@/contexts/AuthContext";

export interface PropertyViewedPayload {
  property_id: string;
  property_type?: string;
  listing_type?: string;
  location_city?: string;
  location_area?: string;
  price?: number;
  realtor_id?: string;
  developer_id?: string;
  is_managed_property?: boolean;
  traffic_source?: string;
}

export interface PropertySearchedPayload {
  search_term?: string;
  location?: string;
  property_type?: string;
  listing_type?: string;
  bedrooms?: number;
  min_budget?: number;
  max_budget?: number;
  results_count: number;
  sort?: string;
  filters_used?: string[];
}

export interface WhatsAppClickedPayload {
  property_id: string;
  realtor_id?: string;
  developer_id?: string;
  listing_type?: string;
  property_type?: string;
  location_city?: string;
  price?: number;
}

export interface DirectCallClickedPayload {
  property_id: string;
  realtor_id?: string;
  developer_id?: string;
  listing_type?: string;
  property_type?: string;
  location_city?: string;
  price?: number;
}

export interface InspectionClickedPayload {
  property_id: string;
  realtor_id?: string;
  developer_id?: string;
  listing_type?: string;
  property_type?: string;
  location_city?: string;
  price?: number;
}

export interface PropertySavedPayload {
  property_id: string;
  property_type?: string;
  listing_type?: string;
  location_city?: string;
  location_area?: string;
  price?: number;
  realtor_id?: string;
  save_action: "saved" | "unsaved";
}

export interface PropertyRequestSubmittedPayload {
  request_id: string;
  property_type?: string;
  listing_type?: string;
  location?: string;
  min_budget?: number;
  max_budget?: number;
  bedrooms?: number;
  urgency?: string;
  preferred_contact_channel?: string;
}

export interface ReviewSubmittedPayload {
  review_id: string;
  realtor_id: string;
  property_id?: string;
  rating: number;
  interaction_type?: string;
  review_source?: string;
  verified_interaction: boolean;
}

export interface ListingSubmittedPayload {
  listing_id: string;
  property_type?: string;
  listing_type?: string;
  location_city?: string;
  location_area?: string;
  price?: number;
  user_type?: string;
  realtor_id?: string;
  developer_id?: string;
  subscription_plan?: string;
}

export interface ListingApprovedPayload {
  listing_id: string;
  property_type?: string;
  listing_type?: string;
  location_city?: string;
  realtor_id?: string;
  developer_id?: string;
  approval_time?: string;
  review_duration_hours?: number;
  subscription_plan?: string;
}

export interface SubscriptionCompletedPayload {
  subscription_id: string;
  plan_name?: string;
  plan_type?: string;
  amount?: number;
  currency?: string;
  payment_method?: string;
  payment_reference?: string;
  billing_period?: string;
  user_type?: string;
  previous_plan?: string;
  current_plan?: string;
}

export interface InspectionPaymentCompletedPayload {
  inspection_id?: string;
  property_id: string;
  inspection_type?: string;
  amount?: number;
  currency?: string;
  payment_method?: string;
  payment_reference?: string;
  realtor_id?: string;
  location_city?: string;
}

interface PostHogAnalyticsContextType {
  trackPropertyViewed: (payload: PropertyViewedPayload) => void;
  trackPropertySearched: (payload: PropertySearchedPayload) => void;
  trackWhatsAppClicked: (payload: WhatsAppClickedPayload) => void;
  trackDirectCallClicked: (payload: DirectCallClickedPayload) => void;
  trackInspectionClicked: (payload: InspectionClickedPayload) => void;
  trackPropertySaved: (payload: PropertySavedPayload) => void;
  trackPropertyRequestSubmitted: (payload: PropertyRequestSubmittedPayload) => void;
  trackReviewSubmitted: (payload: ReviewSubmittedPayload) => void;
  trackListingSubmitted: (payload: ListingSubmittedPayload) => void;
  trackListingApproved: (payload: ListingApprovedPayload) => void;
  trackSubscriptionCompleted: (payload: SubscriptionCompletedPayload) => void;
  trackInspectionPaymentCompleted: (
    payload: InspectionPaymentCompletedPayload,
  ) => void;
}

const noop = () => undefined;

const NOOP_CONTEXT: PostHogAnalyticsContextType = {
  trackPropertyViewed: noop,
  trackPropertySearched: noop,
  trackWhatsAppClicked: noop,
  trackDirectCallClicked: noop,
  trackInspectionClicked: noop,
  trackPropertySaved: noop,
  trackPropertyRequestSubmitted: noop,
  trackReviewSubmitted: noop,
  trackListingSubmitted: noop,
  trackListingApproved: noop,
  trackSubscriptionCompleted: noop,
  trackInspectionPaymentCompleted: noop,
};

const PostHogAnalyticsContext = createContext<
  PostHogAnalyticsContextType | undefined
>(undefined);

const ANALYTICS: PostHogAnalyticsContextType = {
  trackPropertyViewed: (payload) => posthog.capture("property_viewed", payload),
  trackPropertySearched: (payload) =>
    posthog.capture("property_searched", payload),
  trackWhatsAppClicked: (payload) =>
    posthog.capture("whatsapp_clicked", payload),
  trackDirectCallClicked: (payload) =>
    posthog.capture("direct_call_click", payload),
  trackInspectionClicked: (payload) =>
    posthog.capture("inspection_clicked", payload),
  trackPropertySaved: (payload) => posthog.capture("property_saved", payload),
  trackPropertyRequestSubmitted: (payload) =>
    posthog.capture("property_request_submitted", payload),
  trackReviewSubmitted: (payload) =>
    posthog.capture("review_submitted", payload),
  trackListingSubmitted: (payload) =>
    posthog.capture("listing_submitted", payload),
  trackListingApproved: (payload) =>
    posthog.capture("listing_approved", payload),
  trackSubscriptionCompleted: (payload) =>
    posthog.capture("subscription_completed", payload),
  trackInspectionPaymentCompleted: (payload) =>
    posthog.capture("inspection_payment_completed", payload),
};

export function PostHogAnalyticsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, isDeveloper, isRealtor, status } = useAuth();

  useEffect(() => {
    if (status === "idle" || status === "loading") return;

    if (user?.id) {
      const userType = isDeveloper
        ? "developer"
        : isRealtor
          ? "realtor"
          : "buyer_renter";

      posthog.identify(user.id, {
        email: user.email,
        user_type: userType,
        account_status: user.kyc_status || "pending",
      });
    } else {
      posthog.reset();
    }
  }, [user?.id, user?.email, user?.kyc_status, isDeveloper, isRealtor, status]);

  return (
    <PostHogAnalyticsContext.Provider value={ANALYTICS}>
      {children}
    </PostHogAnalyticsContext.Provider>
  );
}

export function usePostHogAnalytics(): PostHogAnalyticsContextType {
  const context = useContext(PostHogAnalyticsContext);
  if (!context) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "usePostHogAnalytics called outside PostHogAnalyticsProvider; events will be dropped.",
      );
    }
    return NOOP_CONTEXT;
  }
  return context;
}
